'use client'

import type { FormEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchUserToken, loginUser } from '@/client/api/auth'
import { autoConfigureApiKey } from '@/client/api/tokens'
import { setStoredUser, type StoredUser } from '@/client/auth/userStorage'
import { setStoredSystemToken } from '@/client/utils/apiConfig'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface LoginPanelProps {
  onLoginSuccess?: (user: StoredUser | null) => void
}

const resolveUserId = (user: StoredUser | null): string | null => {
  if (!user) {
    return null;
  }
  const { id } = user;
  if (typeof id === 'number' && Number.isFinite(id)) {
    return String(Math.trunc(id));
  }
  if (typeof id === 'string' && id.trim()) {
    return id.trim();
  }
  const candidateUid = (user as { uid?: unknown }).uid;
  if (typeof candidateUid === 'number' && Number.isFinite(candidateUid)) {
    return String(Math.trunc(candidateUid));
  }
  if (typeof candidateUid === 'string' && candidateUid.trim()) {
    return candidateUid.trim();
  }
  const candidateUserId = (user as { user_id?: unknown }).user_id;
  if (typeof candidateUserId === 'number' && Number.isFinite(candidateUserId)) {
    return String(Math.trunc(candidateUserId));
  }
  if (typeof candidateUserId === 'string' && candidateUserId.trim()) {
    return candidateUserId.trim();
  }
  return null;
};

const LoginPanel: React.FC<LoginPanelProps> = ({ onLoginSuccess }) => {
  const { t } = useTranslation('common')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!username.trim() || !password.trim()) {
      setError(t('login.errorMissingFields'))
      return
    }

    setLoading(true)
    setError(null)
    try {
      const result = await loginUser({ username: username.trim(), password })
      if (!result?.success) {
        setError(result?.message || t('login.errorInvalid'))
        return
      }
      const user = (result.data?.user ?? null) as StoredUser | null
      if (!user) {
        setError(t('login.errorInvalid'))
        return
      }
      setStoredUser(user)
      const userId = resolveUserId(user)
      if (userId) {
        try {
          const tokenResponse = await fetchUserToken(userId)
          if (tokenResponse?.success && typeof tokenResponse.data === 'string' && tokenResponse.data.trim()) {
            setStoredSystemToken(tokenResponse.data.trim())
            try {
              await autoConfigureApiKey()
            } catch (autoErr) {
              console.warn('LoginPanel: 自动配置 API Key 失败', autoErr)
            }
          } else {
            console.warn('LoginPanel: 系统访问令牌获取失败', tokenResponse)
          }
        } catch (tokenError) {
          console.warn('LoginPanel: 请求系统访问令牌时出错', tokenError)
        }
      } else {
        console.warn('LoginPanel: 未能解析用户 ID，跳过访问令牌获取')
      }
	      onLoginSuccess?.(user)
	      setPassword('')
	      window.location.reload()
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
          ? (err as { message: string }).message
          : undefined
      setError(message || t('login.errorGeneric'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">{t('login.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('login.subtitle')}</p>
      </div>

      {error && (
        <Alert className="mt-3" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="mt-4 grid gap-4">
        <Label htmlFor="username">{t('login.username')}</Label>
        <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" disabled={loading} />
        <Label htmlFor="password">{t('login.password')}</Label>
        <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" disabled={loading} />
        <Button type="submit" disabled={loading} className="mt-1">
          {loading ? t('login.submitting') : t('login.submit')}
        </Button>
      </form>
    </>
  )
}

export default LoginPanel
